import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { ViewTopInsetContext } from "./window.store";

/**
 * Measures `env(safe-area-inset-top)` in the native view it renders in and hands it to
 * `useTopInset` below. The window's inset, which the safe-area plugin reports, is only the
 * status bar, and only re-read after a rotation. A view's own also holds the iPad's floating
 * tab bar, and follows the window as it resizes, the iPad's zoom included.
 *
 * Only for a view with no native bar showing: under one, the view's inset holds the bar too,
 * which its content already clears.
 */
export const ViewTopInsetProvider = ({ enabled, children }: { enabled: boolean; children: ReactNode }) => {
  const probe = useRef<HTMLDivElement>(null);
  const [inset, setInset] = useState<number>();

  useLayoutEffect(() => {
    const element = probe.current;
    if (!enabled || !element) {
      setInset(undefined);
      return;
    }
    const read = () => setInset(element.getBoundingClientRect().height);
    read();
    // Each native view is its own web view, so the observer comes from the probe's own window.
    const view = element.ownerDocument.defaultView ?? window;
    const observer = new view.ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled]);

  return (
    <>
      {enabled && (
        <div
          ref={probe}
          aria-hidden
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: 0,
            height: "env(safe-area-inset-top, 0px)",
            visibility: "hidden",
            pointerEvents: "none",
          }}
        />
      )}
      <ViewTopInsetContext.Provider value={enabled ? inset : undefined}>{children}</ViewTopInsetContext.Provider>
    </>
  );
};
